import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import promiseImage from "@/assets/promise-image-v2.webp";
import promiseImageMobile from "@/assets/promise-image-mobile.webp";
import shieldCheck from "@/assets/shield-check.svg";

export function PromiseSection() {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);
  
  // Detect if we're on mobile to disable parallax
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section ref={ref} className="w-full" aria-labelledby="promise-heading">
      <div className="flex max-lg:flex-col">
        {/* Content */}
        <motion.div
          className="w-1/2 bg-horizon flex flex-col items-center justify-center text-center px-4 sm:px-8 md:px-12 lg:px-20 3xl:px-28 4xl:px-32 py-32 max-lg:w-full max-lg:py-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="max-w-[480px] flex flex-col items-center">
            <motion.img
              src={shieldCheck}
              alt=""
              aria-hidden="true"
              className="w-24 h-24 mb-6"
              style={{
                filter:
                  "brightness(0) saturate(100%) invert(28%) sepia(98%) saturate(1800%) hue-rotate(220deg) brightness(95%) contrast(95%)",
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
            />
            <h2 id="promise-heading" className="text-h1 text-foreground mb-6">
              Our Promise
            </h2>
            <p className="text-body-lg text-foreground">
              GrandPad makes technology effortless for older adults—strengthening engagement, building connection,
              reducing loneliness, and enabling older adults to take a more active role in their care.{" "}
            </p>
          </div>
        </motion.div>

        {/* Image with Parallax */}
        <div className="w-1/2 min-h-[500px] max-lg:w-full max-lg:min-h-[400px] overflow-hidden">
          <motion.img
            src={promiseImage}
            srcSet={`${promiseImageMobile} 824w, ${promiseImage} 1107w`}
            sizes="(max-width: 1023px) 100vw, 50vw"
            alt="Older adult using GrandPad tablet for a video call with a healthcare provider"
            className="w-full h-full object-cover max-lg:h-full lg:h-[130%]"
            style={{ y: isMobile ? 0 : y }}
            width={1107}
            height={1440}
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </section>
  );
}
